import { useMemo } from 'react';
import { Alert, Modal, Typography } from 'antd';

import TokenInput from '../common/TokenInput';
import { normalizeId } from '../../utils/ids';
import {
	buildFormulaTokens,
	entryScopeOf,
	formatCalculatedValue,
	formulaElementResolver,
	formulaSummary,
	previewFormula,
	previewValuesOf,
	sameStoredValue,
} from '../../utils/formulaTokens';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { countPhrase, entriesOf, lowerLabel } from '../../utils/repeatingGroupEntries';
import { isRepeatingGroup } from '../../config/repeatingGroups';

/**
 * "How was this worked out?" for one calculated element on one record: the
 * formula as pills, each input's current value, and the result — the
 * calculated twin of DrivenSourceModal (which explains a value taken from
 * OTHER records).
 *
 * Two results can show. The stored one is what the API last wrote. When the
 * form's current inputs differ from what that value was computed from, the
 * preview — the same shared module the API runs, over the form's values —
 * says what the next save will store, so the reader is never left guessing
 * why the number has not moved yet. Linked records (dot-walks) are not
 * fetched here; a formula that walks shows the stored result only.
 *
 * A calculated COLUMN of a repeating group (KPR-828, `groupColumn`: { group,
 * columns, parentValues }) is explained for ONE ENTRY: `item` is that entry
 * (its `elements` are the entry's columns), the formula's inputs are the
 * entry's other columns and then the item's elements, and no lookup is followed.
 */

function SummaryRow({ label, children }) {
	return (
		<div className='card-lookup-detail-modal__element'>
			<div className='card-lookup-detail-modal__element-label'>{label}</div>
			<div className='card-lookup-detail-modal__element-value'>{children}</div>
		</div>
	);
}

export default function CalculatedValueModal({ open, onClose, element, item, cardMeta, groupColumn = null }) {

	const cardDefinitions = useOptionalCardDefinitions();
	const drivenFrom = element?.options?.drivenFrom || {};
	const cardId = normalizeId(cardMeta?._id || cardMeta?.id);
	const cardsById = cardDefinitions?.cardsById;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const getElementsForCard = useMemo(() => formulaElementResolver({ cardsById, elementSetsById }, cardMeta ? [cardMeta] : []), [cardsById, elementSetsById, cardMeta]);
	const cardMetaElements = cardMeta?.elements;
	const mergedElements = useMemo(() => {
		const resolved = cardId ? getElementsForCard(cardId) : [];
		return resolved.length ? resolved : (cardMetaElements || []);
	}, [cardId, getElementsForCard, cardMetaElements]);
	const byName = useMemo(() => new Map(mergedElements.filter(el => el?.name).map(el => [el.name, el])), [mergedElements]);

	// One entry's scope for a group's column: its columns first, the item's elements after.
	const scope = useMemo(
		() => (groupColumn ? entryScopeOf(groupColumn.group, groupColumn.columns, { excludeName: element?.name }) : null),
		[groupColumn, element?.name]
	);
	const entryColumns = groupColumn?.columns;
	const columnByName = useMemo(() => new Map((entryColumns || []).filter(col => col?.name).map(col => [col.name, col])), [entryColumns]);

	const tokens = useMemo(
		() => buildFormulaTokens(mergedElements, getElementsForCard, scope
			? { walks: false, groups: true, entry: { columns: entryColumns, label: lowerLabel(groupColumn.group?.label?.singular || groupColumn.group?.name || 'entry'), excludeName: element?.name } }
			: { excludeName: element?.name, groups: true }),
		[mergedElements, getElementsForCard, element?.name, scope, entryColumns, groupColumn]
	);

	const preview = useMemo(() => previewFormula(drivenFrom.formula, {
		mergedElements,
		getElementsForCard,
		targetElement: element,
		values: scope ? (groupColumn.parentValues || {}) : previewValuesOf(item, mergedElements),
		blanks: drivenFrom.blanks,
		now: new Date(),
		...(scope ? { entry: scope, entryValues: item?.elements || {} } : {}),
	}), [drivenFrom.formula, drivenFrom.blanks, mergedElements, getElementsForCard, element, item, scope, groupColumn]);

	const stored = item?.elements?.[element?.name];
	const walks = preview?.analysis?.walks || [];
	const showPreview = preview?.ok && !walks.length && !sameStoredValue(preview.value, stored);

	// An entry's own columns, then the item's elements; each with the value it reads.
	const inputs = [
		...(scope ? (preview?.analysis?.columnRefs || []).map(name => columnByName.get(name)).filter(Boolean).map(col => ({ el: col, value: item?.elements?.[col.name] })) : []),
		...(preview?.analysis?.ownRefs || []).map(name => byName.get(name)).filter(Boolean)
			.map(el => ({ el, value: scope ? groupColumn.parentValues?.[el.name] : item?.elements?.[el.name] })),
	];

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={560}
			destroyOnClose
			title={`How ${element?.label?.singular || 'this field'} is calculated`}
		>
			<div className='card-lookup-detail-modal__elements' style={{ marginBottom: 16 }}>
				<SummaryRow label='Formula'>
					<TokenInput value={drivenFrom.formula || ''} tokens={tokens} ariaLabel='Formula' disabled multiline />
					<Typography.Text type='secondary' className='d-block' style={{ marginTop: 4 }}>
						{formulaSummary(drivenFrom.formula || '', mergedElements, getElementsForCard, scope ? { columns: entryColumns } : undefined)}
					</Typography.Text>
				</SummaryRow>
				{inputs.map(({ el, value }) => (
					<SummaryRow key={el.name} label={el.label?.singular || el.name}>
						{/* A group an element's formula reads is a count, not a list of objects. */}
						{isRepeatingGroup(el) ? countPhrase(el, entriesOf(value).length) : formatCalculatedValue(el, value)}
					</SummaryRow>
				))}
				{walks.length ? (
					<SummaryRow label='Linked records'>
						{walks.map(via => byName.get(via)?.label?.singular || via).join(', ')}
					</SummaryRow>
				) : null}
				<SummaryRow label='Result'>{formatCalculatedValue(element, stored)}</SummaryRow>
				{showPreview ? (
					<SummaryRow label='After save'>
						<span data-testid='calculated-preview'>{preview.text}</span>
					</SummaryRow>
				) : null}
				<SummaryRow label='Blank inputs'>{drivenFrom.blanks === 'empty' ? 'Leave the result blank' : 'Count as zero'}</SummaryRow>
			</div>
			{preview?.warnings?.length ? (
				<Alert type='warning' showIcon message={preview.warnings.join(' · ')} style={{ marginBottom: 12 }} />
			) : null}
			{preview?.analysis?.timeDependent ? (
				<Alert type='info' showIcon message='This formula reads the clock: it refreshes once a day and whenever the record is saved.' />
			) : null}
		</Modal>
	);
}
